import React, { useMemo, useCallback } from 'react';
import {
  Area,
  CartesianGrid,
  ComposedChart,
  Line,
  ReferenceDot,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from 'recharts';
import MetricsRow from '../MetricsRow';
import {
  createBacktestEquityViewModel,
  formatAxisDate,
  formatCurrencyValue,
  formatPercentValue,
  formatSignedCurrencyValue,
  REPLAY_X_AXIS_TICKS,
} from './backtestEquityViewModel';
import './BacktestEquityDisplay.css';

function BacktestEquityTooltip({ active, payload, showReturn }) {
  if (!active || !payload || !payload.length) return null;
  const point = payload[0].payload;
  return (
    <div className="bed-tooltip">
      {point?.date && <div className="bed-tooltip__date">{point.date}</div>}
      <div className="bed-tooltip__value">{formatCurrencyValue(point?.y)}</div>
      {showReturn && point?.returnPct !== null && point?.returnPct !== undefined && (
        <div className="bed-tooltip__return">{formatPercentValue(point.returnPct)}</div>
      )}
    </div>
  );
}

export default function BacktestEquityDisplay({
  mode = 'completed',
  points = [],
  result = {},
  progress = null,
  replayProgress = 1,
  metrics = null,
  showBenchmark = false,
  loading = false,
  error = null,
  height = 336,
  showMetrics = false,
  metricsSize = 'default',
  className = '',
}) {
  const model = useMemo(
    () =>
      createBacktestEquityViewModel({
        mode,
        points,
        result,
        progress,
        replayProgress,
        metrics,
      }),
    [mode, points, result, progress, replayProgress, metrics],
  );

  const tickFormatter = useCallback(
    (value) => {
      if (model.isPlayback) return model.getReplayTickLabel(value);
      return formatAxisDate(value, model.axisDateGranularity);
    },
    [model],
  );

  const chartColor =
    model.trendDirection === 'negative'
      ? '#ef4444'
      : model.trendDirection === 'positive'
        ? '#10b981'
        : '#3b82f6';

  const rootClass = ['bed', `bed--${mode}`, `bed--${model.trendDirection}`, className]
    .filter(Boolean)
    .join(' ');

  const signedGain = formatSignedCurrencyValue(model.gainLoss);
  const signedGainPct = formatPercentValue(model.gainLossPct);
  const finalValue = formatCurrencyValue(model.finalValue);
  const startValue = formatCurrencyValue(model.startValue);
  const chartProgressLabel =
    model.progressPercent === null ? null : Number(model.progressPercent.toFixed(1)).toString();
  const chartAriaLabel =
    mode === 'running' && chartProgressLabel !== null
      ? `Backtest equity chart with ${model.chartData.length} points through ${chartProgressLabel}%`
      : 'Backtest equity chart';
  const isCompletedChart = mode === 'completed';
  const firstChartPoint = model.chartData[0] || null;
  const lastChartPoint = model.chartData[model.chartData.length - 1] || null;

  return (
    <div className={rootClass}>
      {mode === 'completed' ? (
        <div className="bed-completed-summary" aria-label="Backtest starting and final equity">
          <div className="bed-completed-summary__item">
            {model.startDate && (
              <span className="bed-completed-summary__date">{model.startDate}</span>
            )}
            {startValue && <span className="bed-completed-summary__value">{startValue}</span>}
          </div>
          <span className="bed-completed-summary__arrow" aria-hidden="true">
            →
          </span>
          <div className="bed-completed-summary__item bed-completed-summary__item--final">
            {model.endDate && <span className="bed-completed-summary__date">{model.endDate}</span>}
            {finalValue && <span className="bed-completed-summary__value">{finalValue}</span>}
            {signedGain && (
              <span
                className={`bed-completed-summary__gain bed-gain bed-gain--${model.trendDirection}`}
              >
                {signedGain}
              </span>
            )}
          </div>
        </div>
      ) : (
        <div className="bed-live-summary" aria-label="Current simulated backtest equity">
          {finalValue && <div className="bed-live-summary__value">{finalValue}</div>}
          {signedGain && signedGainPct && (
            <div className={`bed-live-summary__gain bed-gain bed-gain--${model.trendDirection}`}>
              {signedGain} ({signedGainPct})
            </div>
          )}
          <div className="bed-live-summary__meta">
            {model.endDate && <span>{model.endDate}</span>}
          </div>
        </div>
      )}

      <div
        className={`bed-chart trend-${model.trendDirection}`}
        data-testid="backtest-chart"
        aria-label={chartAriaLabel}
        style={{ minHeight: height }}
      >
        {loading && <div className="bed-chart__state">Loading chart...</div>}
        {!loading && error && (
          <div className="bed-chart__state bed-chart__state--error">Failed to load chart</div>
        )}
        {!loading && !error && model.chartData.length === 0 && (
          <div
            className="bed-chart__state bed-chart__state--empty"
            aria-label="Backtest chart is waiting for the first equity point"
          />
        )}
        {!loading && !error && model.chartData.length > 0 && (
          <ResponsiveContainer width="100%" height={height}>
            <ComposedChart
              data={model.chartData}
              margin={{ top: isCompletedChart ? 34 : 12, right: 38, left: 6, bottom: 8 }}
            >
              <CartesianGrid
                strokeDasharray="3 3"
                stroke="rgba(255,255,255,0.1)"
                vertical={false}
              />
              <XAxis
                dataKey="chartX"
                type={model.isPlayback ? 'number' : 'category'}
                domain={model.isPlayback ? [0, 100] : undefined}
                ticks={model.isPlayback ? REPLAY_X_AXIS_TICKS : undefined}
                tick={{ fill: '#888', fontSize: 10 }}
                tickFormatter={tickFormatter}
                stroke="rgba(255,255,255,0.2)"
                interval="preserveStartEnd"
                minTickGap={model.isPlayback ? 20 : 36}
                allowDataOverflow={model.isPlayback}
              />
              <YAxis
                domain={isCompletedChart ? model.chartReturnDomain : model.chartDomain}
                tick={{ fill: '#888', fontSize: 10 }}
                tickFormatter={(value) =>
                  isCompletedChart
                    ? `${(value * 100).toFixed(0)}%`
                    : `$${(value / 1000).toFixed(0)}k`
                }
                stroke="rgba(255,255,255,0.2)"
                width={48}
              />
              <RechartsTooltip content={<BacktestEquityTooltip showReturn={isCompletedChart} />} />
              {isCompletedChart && (
                <>
                  <ReferenceLine y={0} stroke="rgba(255,255,255,0.44)" strokeDasharray="6 5" />
                  <Area
                    type="monotone"
                    dataKey="returnPct"
                    baseValue={0}
                    stroke="none"
                    fill={chartColor}
                    fillOpacity={0.12}
                    connectNulls
                    isAnimationActive={false}
                  />
                </>
              )}
              <Line
                type="monotone"
                dataKey={isCompletedChart ? 'returnPct' : 'y'}
                stroke={chartColor}
                strokeWidth={2.5}
                dot={false}
                connectNulls
                isAnimationActive={false}
              />
              {isCompletedChart && firstChartPoint?.returnPct != null && (
                <ReferenceDot
                  x={firstChartPoint.chartX}
                  y={firstChartPoint.returnPct}
                  r={4}
                  fill={chartColor}
                  stroke="#08131f"
                  strokeWidth={2}
                  label={{
                    value: formatPercentValue(firstChartPoint.returnPct, { signed: false }),
                    position: 'top',
                    fill: 'rgba(255,255,255,0.78)',
                    fontSize: 11,
                    fontWeight: 700,
                  }}
                />
              )}
              {isCompletedChart && lastChartPoint?.returnPct != null && (
                <ReferenceDot
                  x={lastChartPoint.chartX}
                  y={lastChartPoint.returnPct}
                  r={5}
                  fill={chartColor}
                  stroke="#08131f"
                  strokeWidth={2}
                  label={{
                    value: formatPercentValue(lastChartPoint.returnPct),
                    position: 'top',
                    fill: chartColor,
                    fontSize: 12,
                    fontWeight: 800,
                  }}
                />
              )}
              {showBenchmark && model.hasBenchmark && (
                <Line
                  type="monotone"
                  dataKey={isCompletedChart ? 'benchmarkReturnPct' : 'benchmark'}
                  stroke="rgba(255,255,255,0.48)"
                  strokeWidth={1.5}
                  strokeDasharray="5 5"
                  dot={false}
                  connectNulls
                  isAnimationActive={false}
                />
              )}
              {model.isPlayback && (
                <Line
                  type="monotone"
                  dataKey="currentY"
                  stroke="transparent"
                  strokeWidth={0}
                  dot={{
                    r: 3,
                    fill: chartColor,
                    stroke: '#0b111c',
                    strokeWidth: 2,
                  }}
                  activeDot={false}
                  isAnimationActive={false}
                />
              )}
            </ComposedChart>
          </ResponsiveContainer>
        )}
      </div>

      {showMetrics && model.metrics && (
        <MetricsRow
          metrics={model.metrics}
          showToggle={false}
          size={metricsSize}
          windowDays={model.elapsedDays}
        />
      )}
    </div>
  );
}
